import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "space-y-6" }
const _hoisted_2 = { class: "rounded-xl border border-catering-cream-300 bg-catering-cream-100 px-6 py-5" }
const _hoisted_3 = { class: "flex items-start gap-4" }
const _hoisted_4 = {
  class: "flex h-10 w-10 shrink-0 items-center justify-center rounded-catering-md bg-gradient-to-br from-catering-lemon to-catering-lemon-300 shadow-catering-xs",
  "aria-hidden": "true"
}
const _hoisted_5 = { class: "mt-4 grid gap-3 sm:grid-cols-3" }
const _hoisted_6 = { class: "flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-catering-ink-900 text-[11px] font-semibold text-catering-lemon" }
const _hoisted_7 = { class: "min-w-0" }
const _hoisted_8 = { class: "block text-sm font-medium text-gray-900" }
const _hoisted_9 = { class: "block text-xs text-gray-500" }
const _hoisted_10 = {
  key: 0,
  class: "text-sm text-red-600"
}
const _hoisted_11 = { class: "grid items-start gap-6 px-6 pb-6 pt-5 xl:grid-cols-[minmax(0,1fr)_420px] xl:gap-7" }
const _hoisted_12 = { class: "min-w-0 space-y-2" }
const _hoisted_13 = {
  key: 0,
  class: "mt-3"
}
const _hoisted_14 = { class: "mt-2 text-sm text-gray-500" }

import { CodeBracketIcon } from '@heroicons/vue/24/outline'
import { computed, onMounted } from 'vue'
import { useStore } from 'vuex'

import ImageUploaderNeue from '@/components/neue/ImageUploaderNeue.vue'
import SelectInput from '@/components/neue/SelectInput.vue'
import SettingCard from '@/components/settings/SettingCard.vue'
import SettingRow from '@/components/settings/SettingRow.vue'
import LeadCaptureFieldRow from '@/components/storefront/LeadCaptureFieldRow.vue'
import { useWebsiteEmbed } from '@/composables/useWebsiteEmbed'
import { useRevealed } from '@/helpers/revelio'
import ChannelDesigner from '@/view/ai/ChannelDesigner.vue'
import ChannelReportLink from '@/view/ai/ChannelReportLink.vue'
import EmbedPreview from '@/view/ai/EmbedPreview.vue'
import { formTestBody, openTestPage, testPageDocument } from '@/view/ai/embedTestPage'
import SiteMini from '@/view/ai/SiteMini.vue'
import WebsiteChannel from '@/view/ai/WebsiteChannel.vue'
import { FORM_COLOR_ELEMENTS } from '@/view/ai/websiteColors'

const MAX_LOGO_BYTES = 2 * 1024 * 1024
const LOGO_NOTE_SHAPE = 'PNG or JPG up to 2MB. Wide or square both work - we fit it without cropping.'
const STALE_TIER_PLACEHOLDER = "Your saved tier can't be used any more - choose another"

// Not a question, so it has no row on the record and no place in the order.

export default {
  __name: 'LeadCaptureEmbed',
  props: {
  // Loaded once by the page around this, and covering both ways in, so the
  // report link works from whichever tab the merchant is standing on.
  reportSummary: { type: Object, default: null }
},
  setup(__props) {



const LOGO_NOTE_WITH_FALLBACK = `${LOGO_NOTE_SHAPE} Uses your storefront logo if you don't upload one.`
const LOGO_NOTE_WITHOUT_FALLBACK = `${LOGO_NOTE_SHAPE} No logo will show until you upload one.`
const LOGO_FIELD = { label: 'Logo', maps_to: 'logo' }

const INTRO_STEPS = [
  { title: 'Switch it on', body: 'Nobody can sign up until you do.' },
  { title: 'Make it yours', body: 'Set the fields, colors and wording. It saves as you go.' },
  { title: 'Embed it', body: 'Copy the code into your site.' }
]

const PASTE_GUIDES = [
  { platform: 'Squarespace', steps: 'Pages → your page → Add block → Code' },
  { platform: 'Wix', steps: 'Settings → Custom Code → Add code to Body' },
  { platform: 'WordPress', steps: 'Edit page → add a Custom HTML block' }
]

const BENEFITS = [
  'Sits inside a page, wherever you paste it',
  'Best in a footer, or on a Newsletter page',
  'Signs people up without sending them away'
]

const FORM_TEXTS = [
  { field: 'title', label: 'Title', align: 'title' },
  { field: 'description', label: 'Subtitle', align: 'description' },
  { field: 'submit_button_text', label: 'Button', align: 'button' }
]

const store = useStore()
const testPageRevealed = useRevealed()

const {
  form,
  fields,
  rows,
  websiteAttrs,
  savedFormEnabled,
  colorDefaults,
  wordingDefaults,
  formPreview,
  syncing,
  saveError,
  logoChange,
  logoFiles,
  uploaderKey,
  tierOptions,
  load,
  applyEdit,
  reset
} = useWebsiteEmbed('lead_capture_page/website')

const logoNote = computed(() =>
  websiteAttrs.value.storefront_logo_url ? LOGO_NOTE_WITH_FALLBACK : LOGO_NOTE_WITHOUT_FALLBACK
)

// A tier that has since stopped being free to hand out is no longer in the list,
// so the select would show blank with no hint that anything changed.
const staleTierPlaceholder = computed(() => {
  const saved = websiteAttrs.value.default_membership_tier_id
  if (!saved) return undefined
  const stillEligible = (websiteAttrs.value.eligible_membership_tiers ?? []).some(tier => tier.id === saved)
  return stillEligible ? undefined : STALE_TIER_PLACEHOLDER
})

const businessName = computed(() => {
  const merchant = store.state.loggedInUser?.attributes ?? {}
  return merchant.user_name || merchant.handle || ''
})

const pendingNote = computed(() => "Your embedded form isn't taking signups yet.")

const openExample = () => {
  const snippet = websiteAttrs.value.form_embed_snippet
  if (!snippet) return

  openTestPage(
    testPageDocument({
      body: formTestBody({ snippet }),
      blurb: 'the form is on it twice: at full height, then inside a 600px box.',
      strapline: 'Newsletter'
    })
  )
}

onMounted(() => load().then(reset))

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("section", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("span", _hoisted_4, [
          _createVNode(_unref(CodeBracketIcon), { class: "h-5 w-5 text-catering-ink-900" })
        ]),
        _cache[7] || (_cache[7] = _createElementVNode("div", { class: "min-w-0" }, [
          _createElementVNode("h2", { class: "text-base font-semibold text-gray-900" }, "Put the same form on your own website"),
          _createElementVNode("p", { class: "mt-1 text-sm text-gray-600" }, " Paste one line of code and visitors sign up without leaving your site. They join the same customer list and get the same text to confirm it. ")
        ], -1))
      ]),
      _createElementVNode("ol", _hoisted_5, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(INTRO_STEPS, (step, index) => {
          return _createElementVNode("li", {
            key: step.title,
            class: "flex items-start gap-2.5 rounded-lg bg-white/80 px-3 py-2.5 ring-1 ring-catering-cream-300"
          }, [
            _createElementVNode("span", _hoisted_6, _toDisplayString(index + 1), 1),
            _createElementVNode("span", _hoisted_7, [
              _createElementVNode("span", _hoisted_8, _toDisplayString(step.title), 1),
              _createElementVNode("span", _hoisted_9, _toDisplayString(step.body), 1)
            ])
          ])
        }), 64))
      ])
    ]),
    (_unref(saveError))
      ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(_unref(saveError)), 1))
      : _createCommentVNode("", true),
    _createVNode(WebsiteChannel, {
      enabled: _unref(form).enabled,
      "onUpdate:enabled": _cache[2] || (_cache[2] = $event => ((_unref(form).enabled) = $event)),
      name: "Embedded form",
      description: "The same signup form, on your own website. Someone fills it in and they join your list, exactly as they would from your page.",
      live: _unref(savedFormEnabled),
      syncing: _unref(syncing),
      snippet: _unref(websiteAttrs).form_embed_snippet,
      "paste-guides": PASTE_GUIDES,
      "can-try": _unref(testPageRevealed),
      "customize-label": "Preview and customize form",
      benefits: BENEFITS,
      "embed-label": "How to embed the form in your site",
      "sizing-note": "The form stretches to fill whatever it sits in. Put the code inside a container on your page and give that container the width or height you want. If it's shorter than the form, the fields scroll and the title stays in view.",
      "paste-note": "The form shows up wherever you paste it. Paste it once per page.",
      "manual-note": "Paste this into the page's HTML where you want the form to appear.",
      "pending-note": pendingNote.value,
      "off-note": "Your embedded form is switched off, so nobody can sign up through it.",
      onTry: openExample
    }, {
      mini: _withCtx(() => [
        _createVNode(SiteMini, {
          channel: "form",
          path: "signup",
          config: _unref(formPreview),
          "site-name": businessName.value
        }, null, 8, ["config", "site-name"])
      ]),
      report: _withCtx(() => [
        _createVNode(ChannelReportLink, {
          summary: __props.reportSummary?.page,
          days: __props.reportSummary?.days,
          to: "/storefront/lead-capture/report",
          "empty-text": "See how many signups turn into paid orders."
        }, null, 8, ["summary", "days"])
      ]),
      customize: _withCtx(() => [
        _createVNode(ChannelDesigner, {
          colors: _unref(form).form_colors,
          "onUpdate:colors": _cache[0] || (_cache[0] = $event => ((_unref(form).form_colors) = $event)),
          alignment: _unref(form).form_alignment,
          "onUpdate:alignment": _cache[1] || (_cache[1] = $event => ((_unref(form).form_alignment) = $event)),
          channel: "form",
          "script-url": _unref(websiteAttrs).form_embed_script_url,
          config: _unref(formPreview),
          defaults: _unref(colorDefaults).form,
          elements: _unref(FORM_COLOR_ELEMENTS),
          texts: FORM_TEXTS,
          "wording-defaults": _unref(wordingDefaults),
          hint: "Fonts follow your site. Click the title, the subtitle or the pencil by the button to change the wording.",
          onEdit: _unref(applyEdit)
        }, null, 8, ["colors", "alignment", "script-url", "config", "defaults", "elements", "wording-defaults", "onEdit"])
      ]),
      _: 1
    }, 8, ["enabled", "live", "syncing", "snippet", "can-try", "pending-note"]),
    _createVNode(SettingCard, {
      name: "Form fields",
      description: "What the embedded form asks for. Drag to reorder."
    }, {
      default: _withCtx(() => [
        _cache[11] || (_cache[11] = _createElementVNode("p", { class: "px-6 pb-4 text-sm text-gray-400" }, " These are the details a signup needs, so they are not renamed or removed - but the order is yours, and so is whether to ask for an email at all. ", -1)),
        _createElementVNode("div", _hoisted_11, [
          _createElementVNode("div", _hoisted_12, [
            _createVNode(LeadCaptureFieldRow, {
              field: LOGO_FIELD,
              draggable: false,
              switchable: "",
              collected: _unref(form).show_logo,
              "onUpdate:collected": _cache[4] || (_cache[4] = $event => (_unref(form).show_logo = $event))
            }, {
              default: _withCtx(() => [
                (_unref(form).show_logo)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                      (_openBlock(), _createBlock(ImageUploaderNeue, {
                        key: _unref(uploaderKey),
                        files: _unref(logoFiles),
                        retained: true,
                        multiple: false,
                        "max-file-size": MAX_LOGO_BYTES,
                        "placeholder-url": _unref(websiteAttrs).storefront_logo_url,
                        accept: "image/png,image/jpeg",
                        onChange: _cache[3] || (_cache[3] = $event => (logoChange.value = $event))
                      }, null, 8, ["files", "placeholder-url"])),
                      _createElementVNode("p", _hoisted_14, _toDisplayString(logoNote.value), 1)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["collected"]),
            _createElementVNode("div", {
              ref_key: "rows",
              ref: rows,
              class: "space-y-2"
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(fields), (field) => {
                return (_openBlock(), _createBlock(LeadCaptureFieldRow, {
                  key: field.key,
                  field: field,
                  switchable: field.maps_to === 'email',
                  requirable: field.maps_to === 'email',
                  collected: field.maps_to === 'email' ? _unref(form).collect_email : true,
                  "onUpdate:required": $event => (field.required = $event),
                  "onUpdate:collected": _cache[5] || (_cache[5] = $event => (_unref(form).collect_email = $event))
                }, null, 8, ["field", "switchable", "requirable", "collected", "onUpdate:required"]))
              }), 128))
            ], 512)
          ]),
          _createElementVNode("div", null, [
            _cache[8] || (_cache[8] = _createElementVNode("div", { class: "mb-2 flex items-center justify-center gap-2 text-xs text-gray-500" }, [
              _createElementVNode("span", { class: "rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600" }, " Preview "),
              _createTextVNode(" Try it out - nothing you send here is submitted. ")
            ], -1)),
            (_unref(websiteAttrs).form_embed_script_url)
              ? (_openBlock(), _createBlock(EmbedPreview, {
                  key: 0,
                  channel: "form",
                  "script-url": _unref(websiteAttrs).form_embed_script_url,
                  config: _unref(formPreview),
                  onEdit: _unref(applyEdit)
                }, null, 8, ["script-url", "config", "onEdit"]))
              : _createCommentVNode("", true),
            _cache[9] || (_cache[9] = _createElementVNode("p", { class: "mt-2.5 text-center text-xs text-gray-500" }, "What a visitor sees on your site", -1)),
            _cache[10] || (_cache[10] = _createElementVNode("p", { class: "mt-1 text-center text-xs leading-snug text-gray-400" }, " Signups from here land on the same list, and show on the same report, as your page. ", -1))
          ])
        ])
      ]),
      _: 1
    }),
    _createVNode(SettingCard, { name: "After they sign up" }, {
      default: _withCtx(() => [
        _createVNode(SettingRow, {
          label: "Default membership tier",
          hint: "New customers who sign up through the embedded form are added to this tier. Tiers with a subscription, meal plan or fee aren't listed."
        }, {
          default: _withCtx(() => [
            _createVNode(SelectInput, {
              modelValue: _unref(form).default_membership_tier_id,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = $event => ((_unref(form).default_membership_tier_id) = $event)),
              "aria-label": "Default membership tier",
              options: _unref(tierOptions),
              placeholder: staleTierPlaceholder.value
            }, null, 8, ["modelValue", "options", "placeholder"])
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ]))
}
}

}